import { Spinner } from "~/components/ui/spinner"
import { DiscordSDK } from "@discord/embedded-app-sdk"
import { AlertTriangleIcon } from "lucide-react"
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState
} from "react"
import { Button } from "~/components/ui/button"
import { api } from "~/trpc/react"

const activityLatchKey = "orbit:discord-activity"
const activityLatchTtlMs = 8 * 60 * 60 * 1000
const activityParamKeys = [
	"frame_id",
	"instance_id",
	"platform",
	"guild_id",
	"channel_id",
	"location_id",
	"custom_id",
	"referrer_id",
	"mobile_app_version"
] as const

const requiredActivityParamKeys = ["frame_id", "instance_id", "platform"]

const readActivityParams = () => {
	if (typeof window === "undefined") return null

	const url = new URL(window.location.href)
	if (requiredActivityParamKeys.every((key) => url.searchParams.has(key))) {
		const params = Object.fromEntries(
			activityParamKeys.flatMap((key) => {
				const value = url.searchParams.get(key)
				return value ? [[key, value]] : []
			})
		)
		sessionStorage.setItem(
			activityLatchKey,
			JSON.stringify({ params, expiresAt: Date.now() + activityLatchTtlMs })
		)
		return params
	}

	const rawLatch = sessionStorage.getItem(activityLatchKey)
	if (!rawLatch) return null

	try {
		const latch = JSON.parse(rawLatch) as {
			params?: Record<string, string>
			expiresAt?: number
		}
		if (
			!latch.expiresAt ||
			latch.expiresAt < Date.now() ||
			!latch.params ||
			!requiredActivityParamKeys.every((key) => latch.params?.[key])
		) {
			sessionStorage.removeItem(activityLatchKey)
			return null
		}

		for (const [key, value] of Object.entries(latch.params)) {
			if (!url.searchParams.has(key)) url.searchParams.set(key, value)
		}
		window.history.replaceState(window.history.state, "", url)
		return latch.params
	} catch {
		sessionStorage.removeItem(activityLatchKey)
		return null
	}
}

const ActivityContext = createContext({
	isActivity: false,
	discordSdk: null as DiscordSDK | null,
	guildId: null as string | null,
	channelId: null as string | null,
	instanceId: null as string | null,
	customId: null as string | null,
	openExternalUrl: (url: string) => {
		window.open(url, "_blank", "noopener,noreferrer")
	}
})

export function useDiscordActivity() {
	return useContext(ActivityContext)
}

export function useOpenExternalUrl() {
	return useDiscordActivity().openExternalUrl
}

export function DiscordActivityProvider({ children }: { children: ReactNode }) {
	const [launchParams] = useState(readActivityParams)
	const [discordSdk, setDiscordSdk] = useState<DiscordSDK | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [status, setStatus] = useState("Initializing Discord Activity")
	const clientIdQuery = api.meta.discordClientId.useQuery(undefined, {
		enabled: Boolean(launchParams)
	})

	useEffect(() => {
		if (!launchParams) return
		let cancelled = false
		const activityGlobal = globalThis as typeof globalThis & {
			__orbitActivityDiscordSdk?: DiscordSDK
			__orbitActivityDiscordSdkClientId?: string
			__orbitActivityDiscordSdkReadyPromise?: Promise<void>
		}
		const withTimeout = async <T,>(
			promise: Promise<T>,
			timeoutMs: number,
			label: string
		) =>
			await Promise.race([
				promise,
				new Promise<T>((_, reject) => {
					setTimeout(
						() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)),
						timeoutMs
					)
				})
			])

		const setupActivitySession = async () => {
			setError(null)
			setStatus("Checking Discord Activity environment")

			try {
				if (clientIdQuery.isLoading && !clientIdQuery.data) {
					setStatus("Loading Discord client id")
					return
				}
				if (clientIdQuery.error) {
					throw new Error(
						`Failed to load Discord client id: ${clientIdQuery.error.message}`
					)
				}

				const clientId = clientIdQuery.data
				if (!clientId) throw new Error("Missing Discord client id")

				if (
					!activityGlobal.__orbitActivityDiscordSdk ||
					activityGlobal.__orbitActivityDiscordSdkClientId !== clientId
				) {
					setStatus("Creating Discord SDK instance")
					activityGlobal.__orbitActivityDiscordSdk = new DiscordSDK(clientId)
					activityGlobal.__orbitActivityDiscordSdkClientId = clientId
					activityGlobal.__orbitActivityDiscordSdkReadyPromise = undefined
				}

				const sdk = activityGlobal.__orbitActivityDiscordSdk
				if (!sdk) throw new Error("Failed to initialize Discord SDK")

				activityGlobal.__orbitActivityDiscordSdkReadyPromise ??= sdk.ready()

				setStatus("Waiting for Discord SDK ready()")
				await withTimeout(
					activityGlobal.__orbitActivityDiscordSdkReadyPromise,
					45_000,
					"discordSdk.ready"
				)

				setStatus("Requesting OAuth code from Discord")
				const { code } = await withTimeout(
					sdk.commands.authorize({
						client_id: clientId,
						response_type: "code",
						state: crypto.randomUUID(),
						prompt: "none",
						scope: ["identify", "email", "guilds"]
					}),
					20_000,
					"discordSdk.commands.authorize"
				)

				setStatus("Exchanging OAuth code with Orbit")
				const tokenResponse = await withTimeout(
					fetch("/api/activity/token", {
						method: "POST",
						credentials: "include",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ code })
					}),
					20_000,
					"fetch /api/activity/token"
				)
				const rawResponse = await tokenResponse.text()
				if (!tokenResponse.ok) {
					throw new Error(
						`Token exchange failed (${tokenResponse.status}): ${rawResponse.slice(0, 250)}`
					)
				}

				const tokenData = JSON.parse(rawResponse) as { access_token?: string }
				if (!tokenData.access_token) {
					throw new Error("Missing access token from Activity token endpoint")
				}

				setStatus("Authenticating with Discord client")
				const auth = await withTimeout(
					sdk.commands.authenticate({ access_token: tokenData.access_token }),
					20_000,
					"discordSdk.commands.authenticate"
				)
				if (!auth) throw new Error("Discord Activity authenticate failed")

				if (cancelled) return
				setStatus("Discord Activity ready")
				setDiscordSdk(sdk)
			} catch (setupError) {
				if (cancelled) return
				console.error("[ACTIVITY BOOTSTRAP] failed", setupError)
				setStatus("Discord Activity login failed")
				setError(
					setupError instanceof Error
						? setupError.message
						: `Failed to initialize Discord Activity: ${String(setupError)}`
				)
			}
		}

		void setupActivitySession()

		return () => {
			cancelled = true
		}
	}, [
		clientIdQuery.data,
		clientIdQuery.error,
		clientIdQuery.isLoading,
		launchParams
	])

	const openExternalUrl = useCallback(
		(url: string) => {
			if (discordSdk) {
				void discordSdk.commands.openExternalLink({ url }).catch((error) => {
					console.error("Discord external link failed", error)
				})
				return
			}
			window.open(url, "_blank", "noopener,noreferrer")
		},
		[discordSdk]
	)

	if (!launchParams) return children

	if (!discordSdk || error) {
		return (
			<div className="w-screen h-screen flex items-center justify-center px-6 bg-orbit-gradient text-text-main">
				{error ? (
					<div className="max-w-lg w-full rounded-lg border border-stroke bg-background-secondary p-6 text-center flex flex-col gap-4 items-center shadow-2xl">
						<AlertTriangleIcon className="size-8 text-yellow-400" />
						<div className="text-lg font-semibold">
							Discord Activity login failed
						</div>
						<div className="text-sm text-text-small">{status}</div>
						<div className="text-xs text-text-small break-words whitespace-pre-wrap max-h-64 overflow-auto text-left w-full rounded bg-black/20 p-3">
							{error}
						</div>
						<Button onClick={() => window.location.reload()}>Retry</Button>
					</div>
				) : (
					<div className="flex flex-col items-center gap-3">
						<Spinner size="xl" />
						<div className="text-sm text-text-small">{status}</div>
					</div>
				)}
			</div>
		)
	}

	return (
		<ActivityContext.Provider
			value={{
				isActivity: true,
				discordSdk,
				guildId: discordSdk.guildId,
				channelId: discordSdk.channelId,
				instanceId: discordSdk.instanceId,
				customId: discordSdk.customId,
				openExternalUrl
			}}
		>
			{children}
		</ActivityContext.Provider>
	)
}
